import {
  Column,
  ColumnDef,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import {
  getPriceStatus,
  isElementScrollbarVisible,
  PriceStatus,
} from '@toggle/helpers';
import clsx from 'clsx';
import React, { ReactNode, useMemo, useRef } from 'react';

import { Icon, IconProps } from '~/components/icon/Icon';

import { Tooltip } from '../../tooltip/Tooltip';
import { PinConfig } from '../table-cell/TableCell';
import { WithNAFallback } from '../table-cell/WithNAFallback';
import { formatDecimalPlaces } from '../utils';
import styles from './TableV2.module.scss';
import {
  ColumnsTypes,
  StatusTypes,
  statusTypes,
  TableData,
  TableRow,
  TableV2Payload,
} from './TableV2.types';
import { TableV2Cell } from './TableV2Cell';
import { TableV2Header } from './TableV2Header';

const MOBILE_MAX_VISIBLE_ROWS = 5;
const MOBILE_ROW_HEIGHT = 40;

const isStatusTypes = (value: unknown): value is StatusTypes<string> => {
  return typeof value === 'string' && statusTypes.includes(value);
};

type PickedIconProps = Pick<IconProps, 'iconName' | 'fillColor'>;
const styleMapping: Record<StatusTypes<string>, PickedIconProps> = {
  Success: {
    iconName: 'CheckmarkCircle',
    fillColor: 'var(--viz-bullish)',
  },
  Failed: {
    iconName: 'CloseCircle',
    fillColor: 'var(--viz-bearish)',
  },
};

const leftAlignedColumns = ['text', 'status'];
export const getCellAlignment = (type: ColumnsTypes) =>
  leftAlignedColumns.includes(type) ? 'left' : 'right';

export type PickedTablePayload = Pick<
  TableV2Payload,
  'columnsMeta' | 'data' | 'stickyHeader'
>;

export type TableV2Props = PickedTablePayload & {
  className?: string;
  customHeader?: ReactNode;
  floatingExpandBtn?: ReactNode;
  showBorder?: boolean;
  sliceIndex?: number;
  isTouchDevice?: boolean;
  onRowClick?: (row: Row<TableRow>) => void;
};

export const TableV2 = ({
  className,
  columnsMeta,
  data,
  stickyHeader = false,
  showBorder = true,
  customHeader,
  sliceIndex,
  floatingExpandBtn,
  isTouchDevice = false,
  onRowClick,
}: TableV2Props) => {
  const containerRef = useRef<HTMLTableElement>(null);

  const columns: ColumnDef<TableRow, TableData>[] = columnsMeta.map(item => ({
    id: item.id,
    header: item.title,
    accessorFn: row => row[item.id],
    cell: ({ getValue }) => {
      const value = getValue();
      const priceStatus =
        item.type === 'directional'
          ? getPriceStatus(+value.value)
          : PriceStatus.Default;
      const columnData = value.value;
      const isStatusType = item.type === 'status' && isStatusTypes(columnData);

      const getColumnData = () => {
        if (item.type === 'number') {
          return formatDecimalPlaces(columnData);
        }

        return columnData;
      };

      return (
        <Tooltip disabled={!value.tooltip} inPortal label={value.tooltip}>
          <div className={styles.cellInner}>
            {value.logo && <img src={value.logo} alt="logo" />}
            <WithNAFallback
              value={
                columnData && (
                  <div
                    className={styles.cellWrapper}
                    data-price-status={priceStatus}
                    data-status={isStatusType ? columnData : undefined}
                  >
                    {isStatusType && (
                      <Icon
                        iconName={styleMapping[columnData].iconName}
                        size={9}
                        fillColor={styleMapping[columnData].fillColor}
                      />
                    )}
                    {getColumnData()}
                  </div>
                )
              }
            />
          </div>
        </Tooltip>
      );
    },
    meta: item,
  }));

  const hasHorizontalScroll = isElementScrollbarVisible(containerRef);
  const pinnedColumns = columnsMeta.filter(p => p.sticky).map(p => p.id);

  const getIsPinned = (column: Column<TableRow>) =>
    hasHorizontalScroll && !!column.getIsPinned();

  const getPinConfig = (column: Column<TableRow>): PinConfig | undefined =>
    getIsPinned(column)
      ? {
          left: column.getStart('left'),
          right: column.getStart('right'),
          isLastPinned: column.getIsLastColumn('left'),
        }
      : undefined;

  const slicedData = useMemo(() => {
    return sliceIndex !== undefined ? data.slice(0, sliceIndex) : data;
  }, [sliceIndex, data]);

  const tableMaxHeight = useMemo(() => {
    if (slicedData.length > 5 && isTouchDevice) {
      return MOBILE_MAX_VISIBLE_ROWS * MOBILE_ROW_HEIGHT;
    }

    return undefined;
  }, [slicedData.length, isTouchDevice]);

  const table = useReactTable({
    data: slicedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: pinnedColumns,
      },
    },
  });

  const { rows } = table.getRowModel();

  return (
    <div className={clsx(styles.container, className)}>
      <div>{customHeader}</div>
      <div
        className={styles.wrapper}
        data-table-v2-wrapper
        data-show-border={showBorder}
        style={
          tableMaxHeight ? { maxHeight: `${tableMaxHeight}px` } : undefined
        }
      >
        <table
          className={styles.styledTanStackTableRoot}
          data-table-v2-table
          data-custom-header={!!customHeader || undefined}
          ref={containerRef}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableV2Header
                key={headerGroup.id}
                getPinConfig={getPinConfig}
                headerGroup={headerGroup}
                stickyHeader={!!stickyHeader}
              />
            ))}
          </thead>
          <tbody>
            {rows.map(row => (
              <TableV2Cell key={row.id} row={row} onRowClick={onRowClick} />
            ))}
          </tbody>
        </table>
        {sliceIndex !== undefined && data.length > sliceIndex && (
          <div className={styles.buttonWrapper}>{floatingExpandBtn}</div>
        )}
      </div>
    </div>
  );
};
